import { useState, useRef, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import gsap from 'gsap';
import Lenis from 'lenis';
import {
  Check, ChevronDown, ArrowRight, Sparkles,
  Armchair, Users, FileDown, Maximize2, LayoutGrid, Infinity,
  Plus, Minus, RotateCcw, Wand2, FileSpreadsheet,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Navigation } from '../../components/landing/NavigationSection';
import { Footer } from '../../components/landing/FooterSection';
import { displayFont, fadeInUp, staggerContainer } from '../../components/landing/shared';
import { TABLE_PLAN_FAQ_ITEMS } from '../../data/faqData';
import { SmallSelect } from '../../components/inputs/SmallSelect';
import { CURRENCIES, CURRENCY_SELECT_OPTIONS } from '../../utils/currencies';
import { BrowserMockup, HeroAnimCanvas, ChapterScene1, ChapterScene2, ChapterScene3 } from '../../components/landing/TablePlanMockup';
import { RoundPreview, RectPreview, SquarePreview } from '../table-plan/components/TableShapePreviews';

// ─── Lenis smooth scroll ───────────────────────────────────────────────────────

function useLenis() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.08, smoothWheel: true });
    import('gsap/ScrollTrigger').then(({ ScrollTrigger }) => {
      gsap.registerPlugin(ScrollTrigger);
      lenis.on('scroll', ScrollTrigger.update);
    });
    gsap.ticker.add((time) => lenis.raf(time * 1000));
    gsap.ticker.lagSmoothing(0);
    return () => { lenis.destroy(); };
  }, []);
}

// ─── Canvas Chapter ─────────────────────────────────────────────────────────────

function CanvasChapter() {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 1024);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    let ctx: gsap.Context;
    import('gsap/ScrollTrigger').then(({ ScrollTrigger }) => {
      gsap.registerPlugin(ScrollTrigger);
      ctx = gsap.context(() => {
        const track  = wrapper.querySelector<HTMLElement>('.canvas-track');
        const copies = Array.from(wrapper.querySelectorAll<HTMLElement>('.canvas-copy'));
        const dots   = Array.from(wrapper.querySelectorAll<HTMLElement>('.canvas-dot'));
        if (!track) return;

        const NUM = 3;
        const browserContent = wrapper.querySelector<HTMLElement>('.browser-content');
        const SCREEN_W = browserContent?.clientWidth ?? 520;

        const slides = Array.from(wrapper.querySelectorAll<HTMLElement>('.canvas-slide'));
        gsap.set(slides, { width: SCREEN_W, flexShrink: 0 });
        gsap.set(track, { width: SCREEN_W * NUM });

        copies.forEach((c, i) => gsap.set(c, { opacity: i === 0 ? 1 : 0, y: i === 0 ? 0 : 28 }));
        dots.forEach((d, i) => gsap.set(d, { width: i === 0 ? 20 : 6, opacity: i === 0 ? 1 : 0.4 }));

        ScrollTrigger.create({
          trigger: wrapper,
          start: 'top top',
          end: 'bottom bottom',
          scrub: 1.0,
          onUpdate(self) {
            const step = self.progress * (NUM - 1);
            gsap.set(track, { x: -SCREEN_W * step });
            copies.forEach((copy, i) => {
              const dist = step - i;
              gsap.set(copy, { opacity: Math.max(0, 1 - Math.abs(dist) * 2.2), y: dist * -24 });
            });
            dots.forEach((dot, i) => {
              const active = Math.abs(step - i) < 0.5;
              gsap.set(dot, { width: active ? 20 : 6, opacity: active ? 1 : 0.4 });
            });
          },
        });
      }, wrapper);
    });
    return () => { ctx?.revert(); };
  }, []);

  const copies = [
    {
      badge: 'Setup',
      heading: <>Up and running<br /><span className="text-primary">in minutes.</span></>,
      body: 'Tell the wizard how many tables you need, pick a shape, set capacity. It generates your entire layout instantly — you just fine-tune from there.',
    },
    {
      badge: 'Guest assignment',
      heading: <>Every seat,<br /><span className="text-primary">every name.</span></>,
      body: 'Select a table and assign guests from your invite list. See how full each table is, who\'s confirmed, and who\'s still maybe — all in one panel.',
    },
    {
      badge: 'Seating list',
      heading: <>Ready to<br /><span className="text-primary">hand over.</span></>,
      body: 'The seating list shows every table at a glance — filled seats, empty seats, dietary flags. Export as PDF or CSV and send it straight to your venue.',
    },
  ];

  const mockupHeight = isMobile ? 210 : 380;
  const copyHeight   = isMobile ? 160 : 260;

  return (
    <div ref={wrapperRef} style={{ height: '350vh' }} className="relative">
      <div
        className="sticky top-0 bg-white dark:bg-background-card flex flex-col justify-center overflow-hidden"
        style={{ height: '100dvh', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-16 grid lg:grid-cols-[1fr_520px] gap-4 lg:gap-16 items-center">
          <div className="relative order-2 lg:order-1" style={{ height: copyHeight }}>
            {copies.map((c, i) => (
              <div key={i} className="canvas-copy absolute inset-0 flex flex-col justify-center">
                <span className="inline-block self-start px-3 py-1 sm:px-3.5 sm:py-1.5 bg-primary/10 rounded-full text-xs font-semibold text-primary mb-3 uppercase tracking-wide">
                  {c.badge}
                </span>
                <h2 className="text-3xl sm:text-4xl lg:text-[3rem] leading-[1.08] text-text-primary mb-3" style={displayFont}>
                  {c.heading}
                </h2>
                <p className="text-sm sm:text-base text-text-secondary leading-relaxed max-w-sm">{c.body}</p>
              </div>
            ))}
          </div>
          <div className="order-1 lg:order-2">
            <BrowserMockup contentHeight={mockupHeight}>
              <div className="canvas-track flex h-full">
                <div className="canvas-slide h-full"><ChapterScene1 /></div>
                <div className="canvas-slide h-full"><ChapterScene2 /></div>
                <div className="canvas-slide h-full"><ChapterScene3 /></div>
              </div>
            </BrowserMockup>
            <div className="flex items-center justify-center gap-1.5 mt-3">
              {copies.map((_, i) => (
                <div key={i} className="canvas-dot h-1.5 rounded-full bg-primary" style={{ width: 6 }} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Bento feature visuals ─────────────────────────────────────────────────────

function TableShapesVisual() {
  const shapes = [
    { label: 'Round',       preview: <RoundPreview selected={true} /> },
    { label: 'Rectangular', preview: <RectPreview  selected={false} /> },
    { label: 'Square',      preview: <SquarePreview selected={false} /> },
  ];
  return (
    <div className="flex gap-3">
      {shapes.map((s, i) => (
        <div
          key={i}
          className={`flex flex-col items-center gap-2 w-24 py-3 rounded-xl border flex-shrink-0 ${i === 0 ? 'border-primary bg-primary/5' : 'border-border bg-background'}`}
        >
          {s.preview}
          <span className={`text-xs font-medium ${i === 0 ? 'text-primary' : 'text-text-secondary'}`}>{s.label}</span>
        </div>
      ))}
    </div>
  );
}

function PdfVisual() {
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="flex items-center gap-1.5 px-3 h-9 border border-border rounded-lg text-sm font-medium text-text-secondary bg-background shadow-sm">
        <FileDown size={14} />
        <span className="text-sm">Export</span>
        <ChevronDown size={12} />
      </div>
      <div className="bg-background-card border border-border rounded-xl shadow-lg w-32">
        <div className="flex items-center gap-2 px-3 py-2.5 bg-primary/5 border-b border-border rounded-t-xl">
          <FileDown size={12} className="text-primary" />
          <span className="text-xs font-medium text-primary">PDF</span>
        </div>
        <div className="flex items-center gap-2 px-3 py-2.5 rounded-b-xl">
          <FileDown size={12} className="text-text-secondary" />
          <span className="text-xs text-text-primary">CSV</span>
        </div>
      </div>
    </div>
  );
}

function SyncedGuestsVisual() {
  return (
    <div className="w-full max-w-[220px] mx-auto bg-background rounded-lg border border-border shadow-sm overflow-hidden">
      {[
        { initials:'EW', name:'Emily Watson',  rsvp:'yes' },
        { initials:'TB', name:'Tom Brown',     rsvp:'yes' },
        { initials:'RN', name:'Ruby Nash',     rsvp:'maybe' },
        { initials:'AC', name:'Alex Clarke',   rsvp:'yes' },
      ].map((g, i) => (
        <div key={i} className={`flex items-center gap-2.5 px-3 py-2 ${i < 3 ? 'border-b border-border' : ''}`}>
          <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-[9px] font-semibold text-primary flex-shrink-0">{g.initials}</div>
          <span className="text-[11px] text-text-primary font-medium flex-1">{g.name}</span>
          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${g.rsvp === 'yes' ? 'bg-success' : 'bg-amber-500'}`} />
        </div>
      ))}
    </div>
  );
}

function ZoomVisual() {
  return (
    <div className="w-full max-w-[180px] mx-auto bg-background rounded-xl border border-border shadow-sm overflow-hidden">
      <div className="px-3 py-2 border-b border-border flex items-center justify-between">
        <span className="text-[10px] text-text-muted">Zoom</span>
        <span className="text-[13px] font-semibold text-text-primary">100%</span>
      </div>
      <div className="px-3 py-2 flex items-center gap-2">
        <div className="flex items-center justify-center w-7 h-7 rounded-lg border border-border text-text-secondary text-sm">−</div>
        <div className="flex-1 h-1.5 bg-border rounded-full relative">
          <div className="absolute left-0 top-0 h-full w-1/2 bg-primary rounded-full" />
          <div className="absolute left-1/2 -top-1 w-3 h-3 rounded-full bg-primary border-2 border-background shadow -translate-x-1/2" />
        </div>
        <div className="flex items-center justify-center w-7 h-7 rounded-lg border border-border text-text-secondary text-sm">+</div>
      </div>
    </div>
  );
}

function MultiplePlansVisual() {
  return (
    <div className="w-full max-w-[200px] mx-auto relative h-[70px]">
      <div className="absolute left-2 top-3 w-[65%] bg-background rounded-lg border border-border shadow-sm px-3 py-2 rotate-[-3deg]">
        <span className="text-[9px] font-semibold text-primary">Plan A · 8 tables</span>
      </div>
      <div className="absolute right-2 top-0 w-[65%] bg-background rounded-lg border border-primary shadow-sm px-3 py-2 rotate-[2deg]">
        <span className="text-[9px] font-semibold text-primary">Plan B · 10 tables</span>
        <div className="inline-flex items-center gap-0.5 ml-1.5 px-1 py-0.5 rounded bg-success/15 text-[8px] font-medium text-success">Active</div>
      </div>
    </div>
  );
}

function UnlimitedVisual() {
  return (
    <div className="w-full max-w-[200px] mx-auto flex flex-col gap-2">
      <div className="bg-background rounded-xl border border-border shadow-sm px-4 py-3 flex items-center justify-between">
        <div>
          <p className="text-[10px] text-text-muted mb-0.5">Tables</p>
          <p className="text-xl font-bold text-text-primary">24</p>
        </div>
        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary text-base font-bold">∞</div>
      </div>
      <div className="bg-background rounded-xl border border-border shadow-sm px-4 py-3 flex items-center justify-between">
        <div>
          <p className="text-[10px] text-text-muted mb-0.5">Guests seated</p>
          <p className="text-xl font-bold text-text-primary">186</p>
        </div>
        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary text-base font-bold">∞</div>
      </div>
    </div>
  );
}

function SetupWizardVisual() {
  return (
    <div className="w-full max-w-[200px] mx-auto bg-background rounded-xl border border-border shadow-sm overflow-hidden">
      <div className="flex justify-center gap-1.5 pt-3 pb-2 border-b border-border">
        {[0,1,2,3].map(i => (
          <div key={i} className={`h-1.5 rounded-full transition-all ${i===1 ? 'w-4 bg-primary' : 'w-1.5 bg-border'}`} />
        ))}
      </div>
      <div className="px-4 py-3 text-center">
        <p className="text-[11px] font-semibold text-text-primary mb-3">How many tables?</p>
        <div className="flex items-center justify-center gap-4 mb-3">
          <div className="w-6 h-6 rounded-full border border-border flex items-center justify-center text-text-secondary text-xs">−</div>
          <span className="text-3xl font-bold text-text-primary tabular-nums">8</span>
          <div className="w-6 h-6 rounded-full border border-border flex items-center justify-center text-text-secondary text-xs">+</div>
        </div>
        <div className="py-1.5 px-3 bg-primary rounded-lg flex items-center justify-center">
          <span className="text-[9px] font-bold text-white">Generate 8 tables →</span>
        </div>
      </div>
    </div>
  );
}

function UndoRedoVisual() {
  return (
    <div className="w-full max-w-[200px] mx-auto flex flex-col gap-2">
      <div className="flex gap-2">
        <div className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border border-primary/40 bg-primary/5 shadow-sm">
          <span className="text-sm leading-none text-primary">↩</span>
          <span className="text-[10px] font-semibold text-primary">Undo</span>
        </div>
        <div className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border border-border bg-background shadow-sm">
          <span className="text-[10px] font-medium text-text-muted">Redo</span>
          <span className="text-sm leading-none text-text-muted">↪</span>
        </div>
      </div>
      <div className="bg-background rounded-lg border border-border shadow-sm overflow-hidden">
        {['Moved Table 3','Seated Sophie J.','Added Table 5'].map((action, i) => (
          <div key={i} className={`flex items-center gap-2 px-3 py-1.5 ${i < 2 ? 'border-b border-border' : ''}`}>
            <span className="text-[9px] text-primary font-bold">↩</span>
            <span className="text-[10px] text-text-secondary">{action}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function CsvExportVisual() {
  return (
    <div className="w-full max-w-[210px] mx-auto bg-background rounded-lg border border-border shadow-sm overflow-hidden">
      <div className="grid grid-cols-3 bg-background-card border-b border-border">
        {['Table','Guest','RSVP'].map(h => (
          <div key={h} className="px-2 py-1.5 text-[8px] font-semibold text-text-secondary border-r last:border-r-0 border-border">{h}</div>
        ))}
      </div>
      {[
        ['Top Table', 'Emma J.',   '✓'],
        ['Top Table', 'James M.',  '✓'],
        ['Table 1',   'Sophie J.', '✓'],
        ['Table 1',   'Alex B.',   '?'],
        ['Table 2',   'Ruby N.',   '?'],
      ].map((row, i, arr) => (
        <div key={i} className={`grid grid-cols-3 ${i < arr.length-1 ? 'border-b border-border' : ''}`}>
          <div className="px-2 py-1.5 text-[9px] text-text-secondary border-r border-border truncate">{row[0]}</div>
          <div className="px-2 py-1.5 text-[9px] text-text-primary border-r border-border truncate">{row[1]}</div>
          <div className={`px-2 py-1.5 text-[9px] font-semibold ${row[2]==='✓' ? 'text-success' : 'text-amber-500'}`}>{row[2]}</div>
        </div>
      ))}
    </div>
  );
}

// ─── Bento grid ────────────────────────────────────────────────────────────────
// Layout on lg (3-col): [2+1] / [1+1+1] / [1+2] / [1+1+1]

const BENTO_ITEMS = [
  {
    span: 'col-span-1 sm:col-span-2 lg:col-span-2',
    visualHeight: 'h-44',
    icon: <LayoutGrid size={20} />,
    title: 'Round, rectangular, square & top table',
    description: 'Mix table shapes to match your venue exactly.',
    visual: <TableShapesVisual />,
  },
  {
    span: 'col-span-1',
    visualHeight: 'h-44',
    icon: <FileDown size={20} />,
    title: 'Print-ready PDF export',
    description: 'Room overview, per-table diagrams, full guest list — one click.',
    visual: <PdfVisual />,
  },
  {
    span: 'col-span-1',
    visualHeight: 'h-44',
    icon: <Users size={20} />,
    title: 'Synced guest list',
    description: 'Guests from your invite flow straight in — no re-entering.',
    visual: <SyncedGuestsVisual />,
  },
  {
    span: 'col-span-1',
    visualHeight: 'h-44',
    icon: <Maximize2 size={20} />,
    title: 'Zoom & pan canvas',
    description: 'Navigate large layouts with pinch zoom and drag.',
    visual: <ZoomVisual />,
  },
  {
    span: 'col-span-1',
    visualHeight: 'h-44',
    icon: <Infinity size={20} />,
    title: 'Unlimited tables & guests',
    description: 'No caps. Seat your entire wedding, however large.',
    visual: <UnlimitedVisual />,
  },
  {
    span: 'col-span-1',
    visualHeight: 'h-44',
    icon: <Armchair size={20} />,
    title: 'Multiple saved plans',
    description: 'Keep several seating layouts and switch between them.',
    visual: <MultiplePlansVisual />,
  },
  {
    span: 'col-span-1 sm:col-span-2 lg:col-span-2',
    visualHeight: 'h-44',
    icon: <Check size={20} />,
    title: 'Works across all events',
    description: 'One purchase. Use it for every event you ever plan.',
    visual: (
      <div className="flex flex-wrap gap-2 justify-center">
        {['Wedding','Birthday','Gala Dinner','Corporate','Engagement','Baby Shower','Anniversary','Retirement'].map(l => (
          <span key={l} className="px-2.5 py-1 rounded-full text-[10px] font-medium bg-background border border-border text-text-secondary">{l}</span>
        ))}
      </div>
    ),
  },
  {
    span: 'col-span-1',
    visualHeight: 'h-44',
    icon: <Wand2 size={20} />,
    title: 'Setup wizard',
    description: 'Tell it how many tables you need — it generates the full layout instantly.',
    visual: <SetupWizardVisual />,
  },
  {
    span: 'col-span-1',
    visualHeight: 'h-44',
    icon: <RotateCcw size={20} />,
    title: 'Undo & redo',
    description: 'Made a mistake? Step back through 50 actions and forward again.',
    visual: <UndoRedoVisual />,
  },
  {
    span: 'col-span-1',
    visualHeight: 'h-44',
    icon: <FileSpreadsheet size={20} />,
    title: 'CSV export',
    description: 'Export a full seating spreadsheet — table, guest, RSVP, dietary and more.',
    visual: <CsvExportVisual />,
  },
];

function BentoFeaturesSection() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <section ref={ref} className="py-24 px-6 bg-background-card border-y border-border">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={false} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5 }}
          className="text-center mb-12">
          <span className="inline-block px-4 py-2 bg-primary/10 rounded-full text-sm font-medium text-primary mb-4">
            Everything included
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl text-text-primary mb-4" style={displayFont}>
            Your room, your way.<br /><span className="text-primary">Everything included.</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {BENTO_ITEMS.map((item, i) => (
            <motion.div
              key={i}
              initial={false}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.05 + i * 0.07 }}
              className={`${item.span} bg-background rounded-2xl border border-border hover:shadow-md transition-shadow overflow-hidden flex flex-col`}>

              <div className={`${item.visualHeight} bg-gradient-to-br from-primary/5 to-primary/10 px-4 py-5 flex items-center justify-center`}>
                {item.visual}
              </div>

              <div className="p-4 border-t border-border">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 text-primary">
                    {item.icon}
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-base sm:text-lg font-semibold text-text-primary mb-0.5">{item.title}</h3>
                    <p className="text-xs sm:text-sm text-text-secondary leading-relaxed">{item.description}</p>
                  </div>
                </div>
              </div>

            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── FAQ ──────────────────────────────────────────────────────────────────────

function TablePlanFAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section id="faq" className="py-20 md:py-32 bg-background">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          variants={staggerContainer}
          initial={false}
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          className="text-center mb-16">
          <motion.span variants={fadeInUp}
            className="inline-block px-4 py-2 bg-primary/10 rounded-full text-sm font-medium text-primary mb-4">
            FAQ
          </motion.span>
          <motion.h2 variants={fadeInUp}
            className="text-3xl md:text-4xl lg:text-5xl text-text-primary mb-4" style={displayFont}>
            Got questions? <span className="text-primary">We have answers.</span>
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-base md:text-lg text-text-secondary max-w-2xl mx-auto">
            Can't find what you're looking for?{' '}
            <a href="mailto:hello@wedsy.co" className="text-primary hover:underline">Get in touch</a>.
          </motion.p>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial={false}
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          className="space-y-4">
          {TABLE_PLAN_FAQ_ITEMS.map((faq, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className="bg-background-card border border-border rounded-xl overflow-hidden">
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full flex items-center justify-between p-6 text-left hover:bg-background-elevated transition-colors"
                aria-expanded={openIndex === index}>
                <span className="font-semibold text-text-primary pr-8">{faq.question}</span>
                {openIndex === index
                  ? <Minus className="w-5 h-5 text-primary flex-shrink-0" />
                  : <Plus className="w-5 h-5 text-text-secondary flex-shrink-0" />}
              </button>
              <motion.div
                animate={openIndex === index
                  ? { height: 'auto', opacity: 1 }
                  : { height: 0, opacity: 0 }}
                initial={false}
                transition={{ duration: 0.2, ease: 'easeInOut' }}
                style={{ overflow: 'hidden' }}>
                <div className="px-6 pb-6 text-text-secondary">{faq.answer}</div>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// ─── Seating Planner Pricing ───────────────────────────────────────────────────

const SEATING_FEATURES = [
  'Unlimited tables and guests',
  'Round, rectangular, square & top tables',
  'Drag-and-drop guest assignment',
  'Print-ready PDF export',
  'CSV export',
  'Multiple saved seating plans',
  'Works across all your events',
];

function SeatingPricingSection() {
  const [currencyCode, setCurrencyCode] = useState('GBP');
  const currency = CURRENCIES.find(c => c.code === currencyCode) ?? CURRENCIES[0];

  return (
    <section id="pricing" className="py-20 md:py-32 bg-background-card border-t border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          variants={staggerContainer}
          initial={false}
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          className="text-center mb-16">
          <motion.span variants={fadeInUp}
            className="inline-block px-4 py-2 bg-primary/10 rounded-full text-sm font-medium text-primary mb-4">
            Pricing
          </motion.span>
          <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl lg:text-5xl text-text-primary mb-4" style={displayFont}>
            Simple pricing. <span className="text-primary">No surprises.</span>
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-base md:text-lg text-text-secondary max-w-2xl mx-auto">
            A one-time add-on that unlocks the full seating planner. No subscription required.
          </motion.p>
        </motion.div>

        <motion.div
          variants={fadeInUp}
          initial={false}
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          className="max-w-md mx-auto mb-6">
          <div className="relative rounded-2xl p-8 bg-background border-2 border-primary flex flex-col">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <div className="inline-flex items-center gap-1 px-3 py-1 bg-primary text-text-inverse text-xs font-medium rounded-full">
                <Sparkles size={12} />
                Limited Time
              </div>
            </div>

            <div className="mb-6 text-center">
              <div className="flex justify-start mb-3">
                <SmallSelect
                  value={currencyCode}
                  onChange={setCurrencyCode}
                  options={CURRENCY_SELECT_OPTIONS}
                />
              </div>
              <div className="flex items-baseline gap-3 justify-center">
                <span className="text-2xl text-text-muted line-through">{currency.symbol}{currency.tablePlanOriginal}</span>
                <span className="text-3xl sm:text-4xl md:text-5xl font-bold text-text-primary">{currency.symbol}{currency.tablePlan}</span>
              </div>
              <div className="flex justify-center mt-2">
                <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-success/10 text-success px-3 py-1 rounded-full">
                  <Check className="w-3 h-3" strokeWidth={2.5} />
                  One-time payment
                </span>
              </div>
              {currencyCode !== 'GBP' && (
                <p className="text-xs text-text-tertiary mt-2">
                  Indicative price · Stripe converts £{CURRENCIES[0].tablePlan} to {currencyCode} at the live rate
                </p>
              )}
            </div>

            <ul className="space-y-3 mb-8 flex-1">
              {SEATING_FEATURES.map((feature, i) => (
                <li key={i} className="flex items-center gap-3">
                  <Check className="w-5 h-5 flex-shrink-0 text-success" />
                  <span className="text-text-primary">{feature}</span>
                </li>
              ))}
            </ul>

            <Link to="/register" className="btn btn-primary w-full text-center">
              Get Seating Planner
            </Link>
            <p className="text-sm text-text-muted text-center mt-3">Included free in the Planner subscription</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ─── Hero ──────────────────────────────────────────────────────────────────────

function HeroSection() {
  return (
    <section className="relative py-24 px-6 bg-background border-b border-border overflow-hidden">
      <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">

        <motion.div initial={false} animate="visible" variants={staggerContainer}>
          <motion.span variants={fadeInUp}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full text-sm font-medium text-primary mb-6">
            <Armchair size={13} /> Wedding Seating Planner
          </motion.span>
          <motion.h1 variants={fadeInUp}
            className="text-5xl sm:text-6xl lg:text-[3.5rem] text-text-primary mb-6 leading-[1.08]" style={displayFont}>
            Every guest,<br /><span className="text-primary">perfectly placed.</span>
          </motion.h1>
          <motion.p variants={fadeInUp} className="text-lg text-text-secondary mb-8 leading-relaxed max-w-md">
            Design your room layout, drag guests onto seats, and export a print-ready PDF — all in one place.
          </motion.p>
          <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4">
            <Link to="/register" className="btn-hero btn-primary hover:shadow-lg w-full sm:w-auto">
              Sign up free <ArrowRight size={16} />
            </Link>
            <Link to="/wedding-seating-planner#pricing" className="btn-hero btn-secondary w-full sm:w-auto">
              See pricing
            </Link>
          </motion.div>
        </motion.div>

        <motion.div
          initial={false}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.2 }}>
          <BrowserMockup contentHeight={400}>
            <HeroAnimCanvas />
          </BrowserMockup>
        </motion.div>

      </div>

    </section>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export function TablePlanFeaturePage() {
  useLenis();
  return (
    <div>
      <Navigation />
      <HeroSection />
      <CanvasChapter />
      <BentoFeaturesSection />
      <TablePlanFAQSection />
      <SeatingPricingSection />
      <Footer />
    </div>
  );
}
